import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import HttpServices from 'src/services/httpService';
import { competitorModuleURL } from 'src/services/urlService';

export default function ProfileSearch({ brandId, platforms, onSelect }) {
  const { t } = useTranslation();
  const tr = (key, fallback) => t(`competitors.${key}`, fallback);
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), [brandId]);
  const search = () => {
    if (busy || name.trim().length < 2) return;
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setBusy(true); setError(''); setResults(null);
    HttpServices.Post({
      url: competitorModuleURL.search, body: { name: name.trim() }, signal: request.signal, timeout: 90000,
      successCallback: (data) => { if (!request.signal.aborted) { setResults(data.profiles || []); setBusy(false); } },
      failureCallback: (message) => { if (!request.signal.aborted) { setError(message); setBusy(false); } },
    });
  };
  return <Stack spacing={2}>
    <Alert severity="info">{tr('search_help', 'Enter a business name or its official website, such as https://buffer.com. Website URLs use no AI tokens. We read public website links; review matches before tracking.')}</Alert>
    <Stack component="form" direction="row" spacing={1} onSubmit={(e) => { e.preventDefault(); search(); }}>
      <TextField fullWidth label={tr('search_name', 'Competitor name or website URL')} value={name} disabled={busy} inputProps={{ maxLength: 120 }} onChange={(e) => { setName(e.target.value); setResults(null); }} />
      <Button type="submit" variant="contained" disabled={busy || name.trim().length < 2}>{busy ? <CircularProgress size={20} /> : tr('search', 'Search')}</Button>
    </Stack>
    {error && <Alert severity="error">{error}</Alert>}
    {busy && <Typography variant="body2" color="text.secondary">{tr('search_progress', 'Finding the website and reading its public social links?')}</Typography>}
    {results?.length === 0 && <Alert severity="info">{tr('search_empty', 'No matching profile links found. Try a more specific name or use manual entry.')}</Alert>}
    {!!results?.length && !results.some((item) => platforms.includes(item.platform)) && <Alert severity="warning">
      {tr('no_supported_search_profiles', 'Competitor profiles were found, but your brand has no eligible connected account on these platforms. Connect your own account under Social Accounts, then return here to track and compare.')}
    </Alert>}
    {results && ['youtube', 'instagram', 'bluesky'].map((platform) => <Box key={platform}>
      <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>{platform === 'youtube' ? 'YouTube' : platform === 'bluesky' ? 'Bluesky' : 'Instagram'}</Typography>
      {!results.some((item) => item.platform === platform) && <Typography variant="caption" color="text.secondary">{tr('not_found', 'No profile found')}</Typography>}
      {results.filter((item) => item.platform === platform).map((item) => <Box key={item.url} sx={{ p: 2, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography fontWeight={600}>{item.name || item.target}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>@{item.target}</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button component="a" href={item.url} target="_blank" rel="noopener noreferrer">{tr('view_source', 'View source profile')}</Button>
          {item.source_url && item.source_url !== item.url && <Button component="a" href={item.source_url} target="_blank" rel="noopener noreferrer">{tr('supporting_source', 'Supporting source')}</Button>}
          <Button disabled={!platforms.includes(platform)} onClick={() => onSelect(item)}>{tr('use_profile', 'Use profile')}</Button>
        </Stack>
        {!platforms.includes(platform) && <Alert severity="info" sx={{ mt: 1 }}>
          {t('competitors.missing_platform_account', {
            platform: platform === 'youtube' ? 'YouTube' : platform === 'bluesky' ? 'Bluesky' : 'Instagram',
            defaultValue: 'Your brand has no eligible {{platform}} account connected. Connect your own {{platform}} account under Social Accounts to track this competitor.',
          })}
          {platform === 'instagram' && <Typography variant="body2" sx={{ mt: 0.5 }}>{tr('instagram_connection_required', 'For Instagram comparison, use a professional Instagram account connected through Facebook.')}</Typography>}
        </Alert>}
      </Box>)}
    </Box>)}
  </Stack>;
}
